import { CircuitAggregationType, CircuitInputType } from "types/circuit";
import type { CircuitSetCompletion } from "types/completed-workout";
import type { Circuit, CircuitCategory, CircuitMetric, Measurement } from "types";
import type { WorkoutTaskCircuitSetMetadata } from "features/Workouts/Workouts.types";
import { CIRCUIT_ROUND_COUNT_MIN } from "features/ExerciseLibrary/CircuitCreate/CircuitCreateForm.config";

class CircuitHelpers {
  static constants = {
    /** Every circuit carries this metric (rounds completed); it is the only metric on a no-input circuit. */
    ROUND_COUNT_FIELD: "roundCount",
  };

  /**
   * @description The i18n key for a circuit category's display label,
   * e.g. `CircuitCategory.SpeedAgility` → `"library.circuit_category_SpeedAgility"`.
   */
  static getCategoryTranslationKey(category: CircuitCategory): string {
    return `library.circuit_category_${category}`;
  }

  /**
   * @description Gets the circuit metric by field name, falling back to the default
   * metric, then the first metric. Mirrors {@link ExerciseHelpers.getMetricByFieldNameOrDefault}
   * but makes no exercise-specific assumptions.
   */
  static getMetricByFieldNameOrDefault(circuit: Circuit, metricField?: string | null): CircuitMetric | undefined {
    return circuit.metrics?.find((metric) => metric.field === metricField) || CircuitHelpers.getDefaultMetric(circuit);
  }

  /**
   * @description Gets the default circuit metric, falling back to the first metric.
   */
  static getDefaultMetric(circuit: Circuit): CircuitMetric | undefined {
    return circuit.metrics?.find((metric) => metric.isDefault) || circuit.metrics?.[0];
  }

  /**
   * @description Lists the circuit metrics that support personal bests (sortType is not null).
   */
  static getPbEnabledMetrics(circuit: Circuit): CircuitMetric[] {
    return circuit.metrics?.filter((metric) => metric.isPbEnabled) ?? [];
  }

  /**
   * @description A no-input circuit has no per-round input metric, so its only circuit
   * metric is `roundCount`. Use this to exclude such circuits from metric-selection surfaces.
   */
  static isNoInputCircuit(circuit: Circuit): boolean {
    return (circuit.metrics ?? []).every((metric) => metric.field === CircuitHelpers.constants.ROUND_COUNT_FIELD);
  }

  /**
   * @description The scored (input-derived) circuit metric — the non-`roundCount` metric that
   * carries the circuit's aggregation and sort type. Undefined for a no-input circuit.
   */
  static getScoredMetric(circuit: Circuit): CircuitMetric | undefined {
    return circuit.metrics?.find((metric) => metric.field !== CircuitHelpers.constants.ROUND_COUNT_FIELD);
  }

  /**
   * @description The value of the circuit's per-round input (time, reps or distance) for each round the athlete did,
   * in round order. `null` for a round that did not record it. Empty for a no-input circuit.
   */
  static getRoundValues(circuit: Circuit, completion: Pick<CircuitSetCompletion, "rounds"> | null): (number | null)[] {
    const roundField = circuit.roundMetrics?.[0]?.field;
    if (!roundField || !completion) return [];

    return completion.rounds.map((round) => round.find((metric) => metric.field === roundField)?.value ?? null);
  }

  /**
   * @description The circuit score of a completed circuit: the value the scored metric recorded and otherwise its
   * aggregation (sum, average, best) of the rounds. `null` for a no-input circuit, or when no round has a value.
   */
  static getScoreValue(circuit: Circuit, completion: CircuitSetCompletion | null): number | null {
    const scoredMetric = CircuitHelpers.getScoredMetric(circuit);
    if (!scoredMetric || !completion) return null;

    const recorded = completion.metrics.find((metric) => metric.field === scoredMetric.field)?.value;
    if (typeof recorded === "number") return recorded;

    const values = CircuitHelpers.getRoundValues(circuit, completion).filter(
      (value): value is number => typeof value === "number"
    );
    if (values.length === 0) return null;

    switch (scoredMetric.aggregationType) {
      case CircuitAggregationType.Sum:
        return values.reduce((total, value) => total + value, 0);
      case CircuitAggregationType.Average:
        return values.reduce((total, value) => total + value, 0) / values.length;
      case CircuitAggregationType.Best:
        // A lower value is the better one when the metric sorts that way (a time), otherwise the higher.
        return scoredMetric.sortType === 0 ? Math.min(...values) : Math.max(...values);
      default:
        return null;
    }
  }

  /**
   * @description A time in seconds as minutes and seconds, `2:58`, with the hours in front from one hour on, `1:02:58`.
   * Fractions of a second are rounded, as a round is timed to the second. `TimeHelper.formatTime` pads the minutes
   * (`02:58`), the design of the review circuits shows `2:58`.
   */
  static formatDuration(seconds: number): string {
    const totalSeconds = Math.max(Math.round(seconds), 0);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const remainingSeconds = String(totalSeconds % 60).padStart(2, "0");

    return hours > 0
      ? `${hours}:${String(minutes).padStart(2, "0")}:${remainingSeconds}`
      : `${minutes}:${remainingSeconds}`;
  }

  /**
   * @description Reconstructs the original {@link CircuitInputType} from a circuit's generated
   * round metric (the API stores only the generated metrics, not the create-time input). Used to
   * seed the read-only "Athlete input" field on the edit form and the preview metadata. Maps the
   * round metric `field` (`repCount`/`duration`/`distance`), disambiguating distance by unit.
   */
  static deriveInputType(circuit: Circuit): CircuitInputType {
    const roundMetric = circuit.roundMetrics?.[0];
    if (!roundMetric) return CircuitInputType.None;

    switch (roundMetric.field) {
      case "repCount":
        return CircuitInputType.Reps;
      case "duration":
        return CircuitInputType.Duration;
      case "distance":
        return /yard/i.test(roundMetric.storageUnit) ? CircuitInputType.DistanceYards : CircuitInputType.DistanceMetres;
      default:
        return CircuitInputType.None;
    }
  }

  /**
   * @description Resolves a circuit metric's value from a measurement. Aggregated circuit
   * metrics live in `measurement.metrics[]`, but `roundCount` is a top-level field that is
   * not guaranteed to appear there — so fall back to `measurement.roundCount` for it.
   */
  static getMeasurementMetricValue(measurement: Measurement, field: string): number | undefined {
    const value = measurement.metrics.find((metric) => metric.field === field)?.value;

    if (typeof value !== "undefined") return value;

    if (field === CircuitHelpers.constants.ROUND_COUNT_FIELD) return measurement.roundCount;

    return undefined;
  }

  /**
   * @description The round count a circuit workout task runs for. The API guarantees a
   * single set-metadata entry carrying the override, so the fallbacks are defensive only:
   * the circuit's own default round count, then the minimum of 1.
   */
  static getTaskRoundCount(
    setMetadata: WorkoutTaskCircuitSetMetadata[],
    circuit?: Pick<Circuit, "roundCount"> | null
  ): number {
    return setMetadata[0]?.roundCount ?? circuit?.roundCount ?? CIRCUIT_ROUND_COUNT_MIN;
  }
}

export default CircuitHelpers;
